import styled, { keyframes } from 'styled-components'
import { Link } from 'react-router-dom'

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`

export const Logo = styled.div`
  height: 50px;
  width: 50px;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.primary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1.5rem;
  letter-spacing: -0.03em;
  text-transform: lowercase;
  box-shadow: ${({ theme }) => theme.colors.cardShadow};
  border: 2px solid ${({ theme }) => theme.colors.border};
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 720px) {
    height: 44px;
    width: 44px;
    font-size: 1.2rem;
  }
`

export const Container = styled.header`
  background: ${({ theme }) => theme.colors.headerGradient};
  height: 70px;
  display: flex;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: ${({ $isScrolled, theme }) => ($isScrolled ? '0 6px 20px rgba(0, 0, 0, 0.18)' : theme.colors.cardShadow)};
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  @media (max-width: 720px) {
    height: 60px;
  }
`

export const Content = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  align-items: center;
  padding: 10px 20px;
  position: relative;
  gap: 20px;

  > a:first-child {
    height: 50px;
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }

    @media (max-width: 720px) {
      height: 44px;
    }
  }

  .title-link {
    text-decoration: none;
    transition: transform 0.3s ease;

    &:hover {
      transform: translateX(5px);
    }

    @media (max-width: 720px) {
      display: none;
    }
  }

  @media (max-width: 720px) {
    justify-content: space-between;
  }

  h3 {
    color: #ffffff;
    font-size: 1.1rem;
    font-weight: 600;
    white-space: nowrap;

    @media (max-width: 960px) {
      font-size: 1rem;
    }
  }
`

export const NavLink = styled(Link)`
  color: ${(props) => (props.$active ? '#fff' : 'rgba(255, 255, 255, 0.9)')};
  text-decoration: none;
  font-weight: ${(props) => (props.$active ? '600' : '400')};
  position: relative;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.3s ease;
  background: ${(props) => (props.$active ? 'rgba(255, 255, 255, 0.15)' : 'transparent')};

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: ${(props) => (props.$active ? '80%' : '0')};
    height: 2px;
    background: white;
    transform: translateX(-50%);
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 80%;
  }
`

export const DropdownItem = styled(Link)`
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  text-decoration: none;
  border-radius: 6px;
  transition: all 0.3s ease;
  font-size: 0.95rem;

  .icon {
    font-size: 1.2rem;
    transition: transform 0.3s ease;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.headerGradient};
    color: white;
    transform: translateX(5px);

    .icon {
      transform: scale(1.2);
    }
  }
`

export const DesktopLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: 25px;
  margin-left: auto;

  @media (max-width: 720px) {
    display: none;
  }

  .dropdown {
    position: relative;
    display: flex;
    align-items: center;
    height: 70px;
    cursor: pointer;

    span {
      color: rgba(255, 255, 255, 0.9);
      text-decoration: none;
      padding: 8px 12px;
      border-radius: 6px;
      transition: all 0.3s ease;
      font-weight: 500;

      &:hover {
        background: rgba(255, 255, 255, 0.2);
        color: #fff;
      }
    }

    .dropdown-content {
      display: none;
      position: absolute;
      top: 100%;
      left: 0;
      background: ${({ theme }) => theme.colors.surface};
      padding: 8px;
      border-radius: 12px;
      box-shadow: ${({ theme }) => theme.colors.cardShadow};
      margin-top: 0;
      z-index: 10;
      min-width: 200px;
      animation: ${fadeIn} 0.3s ease-out;
      border: 1px solid ${({ theme }) => theme.colors.border};
    }

    &:hover .dropdown-content {
      display: block;
    }
  }
`

export const NavMenu = styled.nav`
  display: none;

  @media (max-width: 720px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 60px;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
    width: 75%;
    max-width: 350px;
    box-shadow: -5px 0 20px rgba(0, 0, 0, 0.25);
    padding: 30px 20px;
    gap: 5px;
    overflow-y: auto;
    transform: ${({ $isOpen }) => ($isOpen ? 'translateX(0)' : 'translateX(100%)')};
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
    transition:
      transform 0.3s ease,
      visibility 0.3s ease;
    animation: ${({ $isOpen }) => ($isOpen ? slideIn : 'none')} 0.3s ease-out;
    min-height: 300px;

    a {
      text-decoration: none;
      color: ${({ theme }) => theme.colors.textPrimary};
      padding: 16px 18px;
      border-radius: 8px;
      transition: all 0.3s ease;
      font-size: 1rem;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 12px;
      min-height: 50px;

      &:hover {
        background: ${({ theme }) => theme.colors.headerGradient};
        color: ${({ theme }) => theme.colors.onPrimary};
        transform: translateX(5px);
      }
    }

    strong {
      margin-top: 20px;
      margin-bottom: 8px;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.primary};
      font-size: 0.9rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding-left: 18px;
    }
  }
`

export const MenuIcon = styled.div`
  color: white;
  font-size: 28px;
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
  background: ${(props) => (props.$isOpen ? 'rgba(255, 255, 255, 0.2)' : 'transparent')};

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    transition: transform 0.3s ease;
    transform: ${(props) => (props.$isOpen ? 'rotate(90deg)' : 'rotate(0)')};
  }

  @media (max-width: 720px) {
    display: flex;
  }
`

export const MenuOverlay = styled.div`
  display: none;

  @media (max-width: 720px) {
    display: block;
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.5)')};
    backdrop-filter: blur(4px);
    z-index: 999;
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
    transition:
      opacity 0.3s ease,
      visibility 0.3s ease;
  }
`

export const ThemeToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;

  &:hover {
    background: rgba(255, 255, 255, 0.22);
    transform: translateY(-2px);
  }

  svg {
    font-size: 18px;
  }
`

export const ThemeToggleMobile = styled(ThemeToggle)`
  width: 100%;
  justify-content: center;
  margin-bottom: 10px;
  background: ${({ theme }) => theme.colors.surfaceMuted};
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceElevated};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`

export const BottomNav = styled.nav`
  position: fixed;
  bottom: 0;
  left: calc((100vw - var(--app-shell-width, 100vw)) / 2);
  right: auto;
  width: var(--app-shell-width, 100vw);
  max-width: var(--app-shell-width, 100vw);
  box-sizing: border-box;
  height: 56px;
  background: ${({ theme }) => theme.colors.surface};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: none;
  align-items: center;
  justify-content: space-around;
  z-index: 999;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
  padding: 0 4px;

  @media (max-width: 900px) {
    display: flex;
  }
`

export const BottomNavItem = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  text-decoration: none;
  padding: 10px 14px;
  border-radius: 12px;
  transition: all 0.25s ease;
  border: none;
  background: ${({ $active, theme }) => ($active ? `${theme.colors.primary}15` : 'none')};
  cursor: pointer;
  position: relative;

  svg {
    font-size: 24px;
  }

  span {
    display: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceMuted};
    color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`

export const FloatingSearchButton = styled.button`
  position: fixed;
  bottom: 76px;
  right: calc((100vw - var(--app-shell-width, 100vw)) / 2 + 16px);
  width: 58px;
  height: 58px;
  border-radius: 50%;
  border: none;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  display: none;
  align-items: center;
  justify-content: center;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.96);
  }

  @media (max-width: 900px) {
    display: inline-flex;
  }
`

export const SearchOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(6px);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`

export const SearchPanel = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 16px;
  padding: 20px;
  width: min(560px, 100%);
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.colors.cardShadow};
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const SearchHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  h3 {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`

export const SearchInput = styled.input`
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  background: ${({ theme }) => theme.colors.inputBackground};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 1rem;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    border-color: transparent;
  }
`

export const SearchResults = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  padding-right: 4px;
`

export const SearchResultItem = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  text-decoration: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  transition: all 0.2s ease;

  span {
    font-size: 0.85rem;
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateX(4px);
  }
`

export const SearchClose = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 24px;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) => theme.colors.surfaceMuted};
  }
`

export const AuthButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`

export const AuthButton = styled(Link)`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  white-space: nowrap;

  &.login {
    color: #ffffff;
    background: ${({ theme }) => (theme.mode === 'dark' ? '#6366f1' : theme.colors.primary)};
    border: 2px solid transparent;

    &:hover {
      background: ${({ theme }) => (theme.mode === 'dark' ? '#7c3aed' : theme.colors.primaryHover)};
      transform: translateY(-1px);
    }
  }

  &.register {
    color: #ffffff;
    background: ${({ theme }) => (theme.mode === 'dark' ? '#6366f1' : theme.colors.primary)};
    border: 2px solid transparent;

    &:hover {
      background: ${({ theme }) => (theme.mode === 'dark' ? '#7c3aed' : theme.colors.primaryHover)};
      transform: translateY(-1px);
    }
  }
`

export const UserMenuContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`

export const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#6366f1' : theme.colors.primary)};
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  flex-shrink: 0;
  text-transform: uppercase;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`

export const UserMenuButton = styled.button`
  padding: 0;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    opacity: 0.8;
  }

  svg {
    flex-shrink: 0;
    font-size: 1rem;
  }
`

export const UserDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 1000;
  overflow: hidden;
  animation: ${fadeIn} 0.2s ease;
`

export const UserDropdownHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceElevated};

  .email {
    font-size: 0.85rem;
    color: ${({ theme }) => theme.colors.textSecondary};
    word-break: break-all;
  }
`

export const UserDropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  text-align: left;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceMuted};
  }

  &.danger {
    color: ${({ theme }) => theme.colors.danger};
  }
`

export const UsageBadge = styled.div`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${({ $variant, theme }) => {
    if ($variant === 'unlimited') return theme.mode === 'dark' ? '#10b98133' : '#10b98120'
    if ($variant === 'warning') return theme.mode === 'dark' ? '#f59e0b33' : '#f59e0b20'
    if ($variant === 'danger') return theme.mode === 'dark' ? '#ef444433' : '#ef444420'
    return theme.colors.surfaceMuted
  }};
  color: ${({ $variant, theme }) => {
    if ($variant === 'unlimited') return theme.colors.success
    if ($variant === 'warning') return '#f59e0b'
    if ($variant === 'danger') return theme.colors.danger
    return theme.colors.textSecondary
  }};
  border: 1px solid
    ${({ $variant, theme }) => {
      if ($variant === 'unlimited') return theme.colors.success
      if ($variant === 'warning') return '#f59e0b'
      if ($variant === 'danger') return theme.colors.danger
      return theme.colors.border
    }};
`
